import { z } from "zod";
import { MachineTypeSchema } from "./machines.js";
import { LAYOUT_NODE_TYPES, LAYOUT_EDGE_TYPES } from "./layout.js";
import { DraftParameterSchema, type DraftParameter } from "./draft-parameter.js";
import { PairSimProfileSchema } from "./compatibility.js";
import { TmpIdSchema, type TmpId } from "./draft-line-tmp.js";
import {
  checkAtomicCanvas,
  checkAtomicLimits,
  checkAtomicMachineCodes,
} from "./draft-line-refinements.js";

export { TmpIdSchema, DraftParameterSchema };
export type { TmpId, DraftParameter };

export const DRAFT_LIMITS = {
  machines: 50,
  parameters: 200,
  sensorLinks: 100,
  compatibilities: 100,
  edges: 100,
} as const;

export const DraftHeaderSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional().default(""),
  responsible: z.string().max(120).optional().default(""),
  // Spec 081 (FR-019) — a unidade de produção da linha é obrigatória também na criação atômica:
  // uma linha criada pelo editor não pode nascer sem o rótulo de tudo que ela vai medir.
  productionUnit: z.string().min(1).max(20),
});
export type DraftHeader = z.infer<typeof DraftHeaderSchema>;

export const DraftMachineBasicSchema = z.object({
  code: z.string().min(1).max(40),
  name: z.string().min(1).max(120),
  type: MachineTypeSchema,
  requiresOperator: z.boolean().default(false),
  stoppedCostPerHour: z.number().nonnegative().optional(),
  // Spec 080 — o seletor de Modelo 3D é editável na barra lateral desde a 078, mas o campo não
  // existia aqui: "Criar e vincular" relatava sucesso e a máquina nascia como bloco genérico.
  model3dId: z.string().uuid().nullable().optional(),
});
export type DraftMachineBasic = z.infer<typeof DraftMachineBasicSchema>;

export const DraftSensorLinkSchema = z.object({
  tmpId: TmpIdSchema,
  sensorId: z.string().uuid(),
  parameterTmpId: TmpIdSchema.optional(),
});
export type DraftSensorLink = z.infer<typeof DraftSensorLinkSchema>;

export const DraftCompatibilitySchema = z.object({
  tmpId: TmpIdSchema,
  productId: z.string().uuid(),
  enabled: z.boolean().default(true),
  // Perfil de simulação por par (substitui o campo morto `exceptions`).
  simProfile: PairSimProfileSchema.nullable().optional(),
});
export type DraftCompatibility = z.infer<typeof DraftCompatibilitySchema>;

export const DraftMachineSchema = z
  .object({
    tmpId: TmpIdSchema,
    basic: DraftMachineBasicSchema,
    parameters: z.array(DraftParameterSchema).default([]),
    sensorLinks: z.array(DraftSensorLinkSchema).default([]),
    compatibilities: z.array(DraftCompatibilitySchema).default([]),
  })
  // Spec 080 — um papel visual ≠ NONE por máquina. O índice parcial
  // `parameter_visual_role_per_machine_uniq` continua sendo a verdade final, mas aqui o conflito é
  // pego ANTES da transação atômica: lá dentro ele viraria um P2002 no meio da criação da linha
  // inteira, sem dizer quais parâmetros disputam o papel.
  .superRefine((machine, ctx) => {
    const seen = new Map<string, string>();
    machine.parameters.forEach((p, i) => {
      const role = "visualRole" in p ? (p.visualRole ?? "NONE") : "NONE";
      if (role === "NONE") return;
      const holder = seen.get(role);
      if (holder) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["parameters", i, "visualRole"],
          message: `"${holder}" já ocupa este papel nesta máquina. Só um parâmetro por papel.`,
        });
        return;
      }
      seen.set(role, p.name);
    });
  });
export type DraftMachine = z.infer<typeof DraftMachineSchema>;

export const DraftCanvasNodeSchema = z.object({
  tmpId: TmpIdSchema,
  type: z.enum(LAYOUT_NODE_TYPES),
  label: z.string().min(1).max(120),
  x: z.number().finite(),
  y: z.number().finite(),
  machineTmpId: TmpIdSchema.nullable().optional(),
  data: z.record(z.unknown()).default({}),
});
export type DraftCanvasNode = z.infer<typeof DraftCanvasNodeSchema>;

export const DraftCanvasEdgeSchema = z.object({
  tmpId: TmpIdSchema,
  sourceNodeTmpId: TmpIdSchema,
  targetNodeTmpId: TmpIdSchema,
  type: z.enum(LAYOUT_EDGE_TYPES).default("PRODUCTION_FLOW"),
  data: z.record(z.unknown()).default({}),
});
export type DraftCanvasEdge = z.infer<typeof DraftCanvasEdgeSchema>;

export const CreateLineAtomicRequestSchema = z
  .object({
    header: DraftHeaderSchema,
    machines: z.array(DraftMachineSchema).max(DRAFT_LIMITS.machines, {
      message: `Máximo de ${DRAFT_LIMITS.machines} máquinas por linha`,
    }),
    canvas: z.object({
      nodes: z.array(DraftCanvasNodeSchema),
      edges: z.array(DraftCanvasEdgeSchema).max(DRAFT_LIMITS.edges, {
        message: `Máximo de ${DRAFT_LIMITS.edges} conexões`,
      }),
    }),
  })
  .superRefine((data, ctx) => {
    checkAtomicLimits(data, ctx, DRAFT_LIMITS, z);
    checkAtomicCanvas(data, ctx, z);
    checkAtomicMachineCodes(data, ctx, z);
  });

export type CreateLineAtomicRequest = z.infer<typeof CreateLineAtomicRequestSchema>;

export const IdMapSchema = z.object({
  lineId: z.string().uuid(),
  machines: z.record(z.string().uuid()),
  parameters: z.record(z.string().uuid()),
  sensorLinks: z.record(z.string().uuid()),
  compatibilities: z.record(z.string().uuid()),
  nodes: z.record(z.string().uuid()),
  edges: z.record(z.string().uuid()),
});
export type IdMap = z.infer<typeof IdMapSchema>;

export const CreateLineAtomicResponseSchema = z.object({
  lineId: z.string().uuid(),
  idMap: IdMapSchema,
  line: z.unknown(),
});
export type CreateLineAtomicResponse = z.infer<typeof CreateLineAtomicResponseSchema>;

export const CREATE_LINE_ATOMIC_ERROR_CODES = [
  "IDEMPOTENCY_KEY_REQUIRED",
  "IDEMPOTENCY_KEY_INVALID",
  "VALIDATION_ERROR",
  "FORBIDDEN",
  "CODE_CONFLICT",
  "INVALID_SENSOR_REF",
  "INVALID_PRODUCT_REF",
  "PAYLOAD_TOO_LARGE",
  "INTERNAL_ERROR",
] as const;
export type CreateLineAtomicErrorCode = (typeof CREATE_LINE_ATOMIC_ERROR_CODES)[number];
